import React from 'react';
import { Checkbox } from '@parrot-software/design-system';

import type { FrontendPermissionId } from '@personnel/domain/types/PermissionsConst';
import { PermissionDebugInfo } from './PermissionDebugInfo';
import { usePermissionsDebug } from '@personnel/presentation/hooks/usePermissionsDebug';

/**
 * Permission Checkbox Component
 *
 * Renders a checkbox control for a permission.
 * Used for independent permissions that can be toggled on/off.
 *
 * @param {PermissionCheckboxProps} props - Component properties
 * @returns {React.ReactElement} Permission checkbox component
 *
 * @example
 * <PermissionCheckbox
 *   permissionId="org-menu"
 *   label="Menú"
 *   description="Permite acceder al módulo de menú"
 *   backendIds={['admin.store']}
 *   checked={true}
 *   onChange={(checked) => console.log(checked)}
 * />
 */

interface PermissionCheckboxProps {
  /** Permission identifier */
  permissionId: FrontendPermissionId;
  /** Permission label */
  label: string;
  /** Optional description */
  description?: string;
  /** Backend permission identifiers */
  backendIds: readonly string[];
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Callback when checkbox changes */
  onChange: (checked: boolean) => void;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
}

const styles = {
  container: 'flex items-start gap-2 py-2',
  checkbox: 'shrink-0 mt-0.5',
  content: ' flex flex-col gap-1 flex-1',
  label: 'text-sm font-medium text-foreground cursor-pointer',
  description: 'text-xs text-muted-foreground whitespace-pre-line',
};

export const PermissionCheckbox = ({
  permissionId,
  label,
  description,
  backendIds,
  checked,
  onChange,
  disabled = false,
}: PermissionCheckboxProps): React.ReactElement => {
  const { isDebugMode } = usePermissionsDebug();

  // Generate unique ID for label association
  const checkboxId = `checkbox-${permissionId}`;

  return (
    <div className={styles.container}>
      <Checkbox
        checked={checked}
        className={styles.checkbox}
        disabled={disabled}
        id={checkboxId}
        onCheckedChange={(checked) => onChange(checked as boolean)}
      />

      <div className={styles.content}>
        <label className={styles.label} htmlFor={checkboxId}>
          {label}
        </label>

        {description && <p className={styles.description}>{description}</p>}

        {/* Debug info - Only shown in development when enabled via console */}
        {isDebugMode && (
          <PermissionDebugInfo backendIds={backendIds} componentType="checkbox" frontendId={permissionId} />
        )}
      </div>
    </div>
  );
};
